import type { PhotoViewsBody } from "@/lib/api/photoEngagement";
import { resolveWelcomeOfferConfig, type EditionBusinessKey } from "@/lib/api/openingsBusiness";
import { getVisitorIp } from "@/lib/api/visitorContext";
import type { BusinessServiceCategory } from "./types";
import type {
  OpeningsBusiness,
  OpeningsAppointmentHistory,
  OpeningsAppointmentReview,
  OpeningsBlogPostDetailResponse,
  OpeningsBlogPostsListResponse,
  OpeningsCancelAppointmentResult,
  OpeningsCreateAppointmentResult,
  OpeningsCreateCustomerResult,
  OpeningsCustomerGate,
  OpeningsListCustomerAppointmentsResponse,
  OpeningsMemberDetails,
  OpeningsMemberOpenings,
  OpeningsNextAvailabilityItem,
  OpeningsPhotoCategoriesResponse,
  OpeningsPhotoLikeResult,
  OpeningsPhotoLikesResponse,
  OpeningsPhotosListResponse,
  OpeningsRescheduleAppointmentResult,
  OpeningsSchedule,
  OpeningsScheduleDetail,
  OpeningsTeamPageMember,
  OpeningsService,
  OpeningsUserPage,
  OpeningsVerificationResult,
  OpeningsVerifyCodeResult,
  OpeningsWelcomeCodeMintResult,
  OpeningsWelcomeCodeRevokeResult,
} from "./openingsTypes";

import {
  getSvcCustomerAppointments,
  isOpeningsSvcConfigured,
} from "./openingsSvc";

const DEFAULT_OPENINGS_API_BASE = "https://api.openings.link";

const getOpeningsApiBase = () => {
  if (typeof window !== "undefined") return "/api/openings";
  return (
    process.env.OPENINGS_API_BASE?.replace(/\/$/, "") ??
    DEFAULT_OPENINGS_API_BASE
  );
};

/**
 * Next.js data-cache TTL for read-only public content (business, schedules,
 * services, member profiles). These endpoints are rate-limited per client IP
 * by the Openings API, and all server-side fetches leave through Vercel's
 * shared egress IPs — so uncached crawler sweeps of the stylist pages burn
 * through the shared budget and 429 for everyone. A short TTL keeps content
 * fresh while letting the data cache absorb bursts; on a failed revalidation
 * Next serves the last cached payload instead of crashing the page.
 *
 * Real-time data (availability, appointment history, booking mutations,
 * anything customer-specific) must stay uncached — omit `revalidate` there.
 */
const PUBLIC_CONTENT_REVALIDATE_SECONDS = 300;

type OpeningsFetchOptions = {
  /** Cache the response in the Next.js data cache for this many seconds. */
  revalidate?: number;
};

/**
 * Undici error codes for connections that died before a response existed —
 * the Vercel↔Fly path sheds a socket every few minutes (TLS handshake
 * resets at the Fly edge; happens even with machines pinned always-on).
 * Retries absorb the blip.
 *
 * NOTE: fetches cached with `next: { revalidate }` revalidate in the
 * background inside Next's data cache — failures there never reach this
 * retry loop (Next swallows them and serves stale). Those are retried at
 * the dispatcher level instead; see `src/instrumentation.ts`.
 */
const TRANSIENT_NETWORK_CODES = new Set([
  "ECONNRESET",
  "ECONNREFUSED",
  "EPIPE",
  "ETIMEDOUT",
  "UND_ERR_CONNECT_TIMEOUT",
  "UND_ERR_SOCKET",
]);

const isTransientNetworkError = (error: unknown): boolean => {
  let current: unknown = error;
  for (let depth = 0; depth < 5 && current instanceof Error; depth += 1) {
    const code = (current as { code?: unknown }).code;
    if (typeof code === "string" && TRANSIENT_NETWORK_CODES.has(code)) {
      return true;
    }
    if (current.name === "SocketError") return true;
    current = current.cause;
  }
  return false;
};

/** Innermost code/name in the cause chain, for retry log lines. */
const describeNetworkError = (error: unknown): string => {
  let label = "unknown";
  let current: unknown = error;
  for (let depth = 0; depth < 5 && current instanceof Error; depth += 1) {
    const code = (current as { code?: unknown }).code;
    if (typeof code === "string") label = code;
    else if (current.name !== "TypeError" && current.name !== "Error") {
      label = current.name;
    }
    current = current.cause;
  }
  return label;
};

/**
 * Blips cluster: Vercel logs show up to 4-5 failed fetches within one render,
 * so a single immediate retry often lands inside the same blip. Two retries
 * with growing delay ride it out. Logged as warnings so the Vercel dashboard
 * shows how often retries fire (and rescue the request) vs errors escaping.
 */
const RETRY_DELAYS_MS = [250, 750];

/**
 * Openings rate-limits `/v1/verifications/send` (and friends) per caller IP,
 * and every server-side fetch from this app leaves through Vercel's shared
 * egress IP — so the whole site would share one bucket. Openings honours
 * `x-openings-client-ip` only when `x-openings-proxy-secret` matches its
 * `TRUSTED_PROXY_SECRET`, so sending neither header (secret unset, or no
 * visitor IP on the current request context) is always a safe no-op — the
 * same contract the browser-facing proxy already relies on at
 * `src/app/api/openings/[...path]/route.ts`.
 *
 * Callers MUST only splice this into the **uncached** branch of
 * `openingsFetch`. Next's data cache keys a fetch on its full options
 * including headers, so attaching a per-visitor header to a cached fetch
 * fragments the 300s shared-content cache per visitor instead of protecting
 * the shared Openings budget.
 */
const visitorProxyHeaders = (): Record<string, string> => {
  const secret = process.env.OPENINGS_PROXY_SECRET;
  const ip = getVisitorIp();
  if (!secret || !ip) return {};
  return {
    "x-openings-proxy-secret": secret,
    "x-openings-client-ip": ip,
  };
};

const openingsFetch = async <T>(
  path: string,
  init?: RequestInit,
  options?: OpeningsFetchOptions,
): Promise<T> => {
  const request = () =>
    fetch(`${getOpeningsApiBase()}${path}`, {
      ...(options?.revalidate !== undefined
        ? { next: { revalidate: options.revalidate } }
        : { cache: "no-store" as const }),
      ...init,
      // No caller in this file passes `init.headers`. If one ever does, note
      // that it spreads last and so can overwrite what is set above —
      // including `x-openings-proxy-secret` / `x-openings-client-ip`, i.e. it
      // could forge or blank the visitor identity sent to Openings. Give a
      // new caller a header set that does not collide with those; do not
      // treat "caller override wins" as a supported feature here.
      headers: {
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(options?.revalidate === undefined ? visitorProxyHeaders() : {}),
        ...(init?.headers ?? {}),
      },
    });

  // GET-only: a mutation's socket can die AFTER the API processed it, so
  // retrying could double-book; reads are safe to re-issue.
  const method = (init?.method ?? "GET").toUpperCase();

  let res: Response | undefined;
  for (let attempt = 0; ; attempt += 1) {
    try {
      res = await request();
      break;
    } catch (error) {
      const delay = RETRY_DELAYS_MS[attempt];
      if (
        method !== "GET" ||
        delay === undefined ||
        !isTransientNetworkError(error)
      ) {
        throw error;
      }
      // Path only, query stripped — `path` can carry a caller-supplied
      // identifier as a query param (e.g. `getPhotos`'s `deviceId`), and this
      // line has no reason to put that in Vercel's logs on every transient
      // retry (review item 7a, mobile-bff Part A review).
      console.warn(
        `[openings] transient ${describeNetworkError(error)} on GET ${path.split("?")[0]}; ` +
          `retry ${attempt + 1}/${RETRY_DELAYS_MS.length} in ${delay}ms`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    const message =
      typeof json?.message === "string"
        ? json.message
        : typeof json?.error?.message === "string"
          ? json.error.message
          : `Openings API request failed with ${res.status}`;
    throw Object.assign(new Error(message), {
      status: res.status,
      body: json,
      retryAfter: res.headers?.get("retry-after"),
    });
  }

  if (json && typeof json === "object" && "data" in json) {
    return json.data as T;
  }

  return json as T;
};

type OpeningsAppointmentMetadata = Record<string, string>;

/**
 * HTTP timeout for BOTH welcome-coupon issuance calls (mint and revoke).
 *
 * Controller ruling, Task 2 review of `src/lib/mobile/welcomeCoupon.ts`:
 * these two requests MUST return well inside
 * `WELCOME_CLAIM_GUARD_TTL_SECONDS` (120s — that constant's own doc in
 * `welcomeCoupon.ts` has the full mechanism). In short: the claim guard is
 * the only thing serializing two concurrent taps naming different
 * businesses for the same phone, and it is TTL-based rather than held for
 * the request's lifetime. A mint or revoke that hangs past 120s lets the
 * guard expire while that claim is still in flight, so a second tap can
 * acquire a fresh guard, read a ledger the first claim has not written yet,
 * and mint a second LIVE code with nothing revoked — the exact double-live-
 * code race the guard exists to close, reopened by a slow HTTP call. 30s
 * leaves generous margin under 120s even for the SWITCH path, which makes
 * TWO of these calls (a revoke, then a mint) back to back within one claim.
 *
 * This file does not import `welcomeCoupon.ts` (by design — see that
 * module's own doc on why the minter is injected rather than imported), so
 * the TTL is named here only in prose, not as a shared constant.
 */
const WELCOME_OFFER_HTTP_TIMEOUT_MS = 30_000;

/**
 * Thrown by `mintWelcomeCode` / `revokeWelcomeCode` when `business`'s
 * welcome-offer id + secret are not configured (`resolveWelcomeOfferConfig`
 * returned `null`). Distinguishable from every other error this file
 * throws via `error.code === "NOT_CONFIGURED"`, so a caller — Task 5's GET
 * route among them — can map straight to "not configured" (`state: "none"`)
 * instead of a 5xx. Thrown BEFORE any network call, so a missing/blank
 * config never produces a partial request. The message never repeats an
 * env var's VALUE (this branch runs only when `resolveWelcomeOfferConfig`
 * found nothing to repeat — there is no secret in scope to leak).
 */
const notConfiguredError = (business: EditionBusinessKey): Error =>
  Object.assign(
    new Error(`Openings welcome offer is not configured for "${business}"`),
    { code: "NOT_CONFIGURED" },
  );

export const OpeningsJson = {
  // No default slug. This is the lowest layer, and a caller that forgot which
  // business it meant would silently resolve Eddie's — the exact mistake the
  // registry exists to make impossible. Every caller passes an explicit handle
  // from `BUSINESSES[key].handle`.
  getBusinessBySlug: (slug: string) =>
    openingsFetch<OpeningsBusiness>(
      `/v1/salons/by-slug?slug=${encodeURIComponent(slug)}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  getAppointmentCount: (businessId: string) =>
    openingsFetch<{ count: number }>(
      `/v1/salons/${encodeURIComponent(businessId)}/appointment-count`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  getScheduleDetail: (scheduleId: string) =>
    openingsFetch<OpeningsScheduleDetail>(
      `/v1/locations/${encodeURIComponent(scheduleId)}/detail`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  getBusinessSchedules: (businessId: string) =>
    openingsFetch<{ items: OpeningsSchedule[] }>(
      `/v1/locations?salonId=${encodeURIComponent(businessId)}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  // Team order and tenure must reflect dashboard saves on the next page load.
  getTeamPage: (businessId: string) =>
    openingsFetch<{ members: OpeningsTeamPageMember[] }>(
      `/v1/salons/${encodeURIComponent(businessId)}/team-page`,
    ),

  getServiceCategories: (businessId: string) =>
    openingsFetch<{ items: BusinessServiceCategory[] }>(
      `/v1/services/categories?salonId=${encodeURIComponent(businessId)}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  getBusinessServices: (businessId: string) =>
    openingsFetch<{ services: OpeningsService[] }>(
      `/v1/services?salonId=${encodeURIComponent(businessId)}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  getUserPage: (args: {
    identifier: string;
    businessId?: string;
    /** Booking validation must read current calendar ownership, not cached display data. */
    fresh?: boolean;
  }) => {
    const query = new URLSearchParams();
    if (args.businessId) query.set("salonId", args.businessId);
    const suffix = query.toString() ? `?${query.toString()}` : "";

    return openingsFetch<OpeningsUserPage>(
      `/v1/users/by-username/${encodeURIComponent(args.identifier)}/page${suffix}`,
      undefined,
      args.fresh ? undefined : { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    );
  },

  getMemberDetails: (memberId: string) =>
    openingsFetch<OpeningsMemberDetails>(
      `/v1/locations/users/${encodeURIComponent(memberId)}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  getCustomerGate: (args: {
    businessId: string;
    customerId: string;
    teamMemberId?: string;
  }) =>
    openingsFetch<{ gate: OpeningsCustomerGate }>("/v1/booking/customer-gate", {
      method: "POST",
      body: JSON.stringify({
        salonId: args.businessId,
        customerId: args.customerId,
        teamMemberId: args.teamMemberId,
      }),
    }),

  createPaymentMethodForBooking: (args: {
    customerId: string;
    paymentId: string;
  }) =>
    openingsFetch<{ success: boolean }>(
      "/v1/customers/payment-method/booking",
      {
        method: "POST",
        body: JSON.stringify({
          customerId: args.customerId,
          paymentId: args.paymentId,
        }),
      },
    ),

  getMemberOpenings: (args: {
    businessId: string;
    scheduleId: string;
    date: string;
    services: { serviceId: string; optionId?: string }[];
  }) => {
    const query = new URLSearchParams({
      salonId: args.businessId,
      scheduleId: args.scheduleId,
      date: args.date,
      services: JSON.stringify(args.services),
      includeVacation: "true",
    });

    return openingsFetch<OpeningsMemberOpenings[]>(
      `/v1/openings/members?${query.toString()}`,
    );
  },

  getNextAvailability: (args: {
    businessId: string;
    scheduleIds: string[];
    teamMemberId?: string;
    date: string;
    serviceDuration: number;
    days?: number;
    /**
     * The selected services (host or member clone ids). Openings leaves out
     * the starts inside their minimum notice, so a time inside it is never
     * offered and then refused. Without them the read is notice-blind.
     */
    serviceIds?: string[];
  }) => {
    const query = new URLSearchParams({
      salonId: args.businessId,
      scheduleIds: args.scheduleIds.join(","),
      date: args.date,
      serviceDuration: String(args.serviceDuration),
    });
    if (args.teamMemberId) query.set("teamMemberId", args.teamMemberId);
    if (args.days) query.set("days", String(args.days));
    if (args.serviceIds?.length) {
      query.set("serviceIds", args.serviceIds.join(","));
    }

    return openingsFetch<OpeningsNextAvailabilityItem[]>(
      `/v1/openings/next-availability?${query.toString()}`,
    );
  },

  getAppointmentHistory: (args: {
    phoneNumber: string;
    businessId: string;
  }) => {
    const query = new URLSearchParams({ salonId: args.businessId });

    return openingsFetch<OpeningsAppointmentHistory>(
      `/v1/appointments/history/${encodeURIComponent(args.phoneNumber)}?${query.toString()}`,
    );
  },

  getVerifiedAppointmentHistory: (args: {
    phoneNumber: string;
    businessId: string;
    code: string;
  }) =>
    openingsFetch<OpeningsAppointmentHistory>(
      "/v1/appointments/history/verified",
      {
        method: "POST",
        body: JSON.stringify({
          salonId: args.businessId,
          phoneNumber: args.phoneNumber,
          code: args.code,
        }),
      },
    ),

  /**
   * A customer's upcoming appointments, via one of two upstream reads:
   *
   * **Preferred: `GET /v1/svc/customer-appointments`** (service-principal
   * authenticated, `read_customer_appointments` capability) whenever the
   * svc credentials are configured — the ONLY read that carries the
   * settled-payment summary, since Openings removed `payment` from the
   * public manage route (its bare-customerId credential is obtainable
   * without OTP; see `manage-appointments-payment-leak.test.ts` over
   * there). Items come back in the narrower `SvcCustomerAppointment` shape
   * and are widened here to `OpeningsCustomerAppointmentItem` so every
   * downstream consumer (`/me/appointments`, the create route's
   * existing-appointment lookup) is untouched; `services` is absent in the
   * svc contract on purpose, and consumers already treat it as optional.
   *
   * **Fallback: `GET /v1/customers/:customerId/manage/appointments`** —
   * public (the `customerId` in the path is itself the access token, per
   * that endpoint's own doc), side-effect-free, and — unlike
   * `getAppointmentHistory` — returns REAL appointment items rather than
   * an always-empty placeholder. Used when svc credentials are unset
   * (rollout), and as a DEGRADATION when the svc read fails: the Me tab
   * losing its whole appointment list to a svc outage would be worse than
   * it briefly losing the payment line, and an absent `payment` already
   * means "render no line" all the way up the stack. The degradation is
   * loud (console.error) so a misconfigured principal cannot hide behind
   * it. Both reads are upcoming-only: each endpoint's own query filters
   * `startsAt >= now`.
   */
  getCustomerAppointments: async (args: {
    customerId: string;
    businessId: string;
  }): Promise<OpeningsListCustomerAppointmentsResponse> => {
    if (isOpeningsSvcConfigured()) {
      try {
        const svc = await getSvcCustomerAppointments(args);
        return {
          items: svc.items.map((item) => ({
            id: item.id,
            startsAt: item.startsAt,
            endsAt: item.endsAt ?? undefined,
            createdAt: item.createdAt ?? undefined,
            status: item.status,
            serviceTitle: item.serviceTitle,
            teamMemberId: item.teamMemberId ?? undefined,
            teamMemberName: item.teamMemberName,
            salonPhoneNumber: item.salonPhoneNumber ?? undefined,
            publicMemberPhoneNumber: item.publicMemberPhoneNumber ?? undefined,
            schedule: item.schedule,
            ...(item.payment ? { payment: item.payment } : {}),
          })),
        };
      } catch (error) {
        console.error(
          "[openings] svc customer-appointments read failed; degrading to the manage route (payment line will be absent)",
          error,
        );
      }
    }
    const query = new URLSearchParams({ salonId: args.businessId });
    return openingsFetch<OpeningsListCustomerAppointmentsResponse>(
      `/v1/customers/${encodeURIComponent(args.customerId)}/manage/appointments?${query.toString()}`,
    );
  },

  getAppointmentReview: (appointmentId: string) =>
    openingsFetch<OpeningsAppointmentReview>(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/review`,
    ),

  addAppointmentNote: (args: {
    appointmentId: string;
    customerId: string;
    note: string;
  }) =>
    openingsFetch<{ appointmentId: string }>("/v1/appointments/note/public", {
      method: "POST",
      body: JSON.stringify(args),
    }),

  createServiceRequest: (args: {
    businessId: string;
    userId?: string;
    teamMemberId?: string;
    scheduleId?: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    note?: string;
    userPhotos: string[];
    referencePhotos: string[];
    serviceIds: string[];
    serviceTitles: string[];
  }) =>
    openingsFetch<{ serviceRequestId: string }>("/v1/service-requests/public", {
      method: "POST",
      body: JSON.stringify({
        salonId: args.businessId,
        userId: args.userId,
        teamMemberId: args.teamMemberId,
        scheduleId: args.scheduleId,
        firstName: args.firstName,
        lastName: args.lastName,
        email: args.email,
        phoneNumber: args.phoneNumber,
        note: args.note,
        userPhotos: args.userPhotos,
        referencePhotos: args.referencePhotos,
        serviceIds: args.serviceIds,
        serviceTitles: args.serviceTitles,
      }),
    }),

  sendVerification: (args: {
    phoneNumber: string;
    businessId: string;
    purpose?: string;
  }) =>
    openingsFetch<OpeningsVerificationResult>("/v1/verifications/send", {
      method: "POST",
      body: JSON.stringify({
        phoneNumber: args.phoneNumber,
        purpose: args.purpose ?? `booking:${args.businessId}`,
      }),
    }),

  cancelVerification: (args: { phoneNumber: string; purpose: string }) =>
    openingsFetch<{ canceled: boolean }>("/v1/verifications/cancel", {
      method: "POST",
      body: JSON.stringify({
        phoneNumber: args.phoneNumber,
        purpose: args.purpose,
      }),
    }),

  verifyCode: (args: {
    phoneNumber: string;
    code: string;
    businessId: string;
  }) =>
    openingsFetch<OpeningsVerifyCodeResult>("/v1/verifications/verify", {
      method: "POST",
      body: JSON.stringify({
        phoneNumber: args.phoneNumber,
        code: args.code,
        purpose: `booking:${args.businessId}`,
      }),
    }),

  createCustomer: (args: {
    businessId: string;
    userId: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
  }) =>
    openingsFetch<OpeningsCreateCustomerResult>("/v1/customers/booking", {
      method: "POST",
      body: JSON.stringify({
        salonId: args.businessId,
        userId: args.userId,
        firstName: args.firstName,
        lastName: args.lastName,
        phoneNumber: args.phoneNumber,
      }),
    }),

  createAppointment: (args: {
    businessId: string;
    userId: string;
    scheduleId: string;
    customerId: string;
    teamMemberId?: string;
    services: { id: string; optionId?: string }[];
    date: string;
    time: string;
    verificationCode?: string;
    skipPaymentCollection?: boolean;
    paymentMethodId?: string;
    metadata?: OpeningsAppointmentMetadata;
    /**
     * Task 7 round-1 review (C1), authorised as an exception to this file's
     * do-not-modify rule: `POST /v1/appointments/create` has its own
     * durable, Postgres-backed Idempotency-Key support (openings-client
     * `apps/api/src/modules/appointments/handlers/createHandler.ts:80-118`)
     * — a 24h record keyed on this header, replaying a verbatim 2xx for a
     * repeated key and `409`ing on a body mismatch; that handler's own
     * comment reads "Optional Idempotency-Key support. Absent header →
     * behavior unchanged." It excludes `verificationCode` from its request
     * hash specifically so a `428` → verify → retry counts as the SAME
     * logical booking.
     *
     * This closes a real gap this app's own idempotency key (`edition-client`
     * `src/lib/mobile/idempotency.ts`) cannot: that key protects against a
     * REPLAY racing a request that is still in flight, but not against a
     * request that already committed upstream before the connection died —
     * `openingsFetch` deliberately never retries a non-GET (see the comment
     * at its retry loop) for exactly that reason, so today the caller's own
     * catch-and-release (`POST /api/mobile/v1/appointments`) reads that as
     * "did not book" and lets a retry create a SECOND real appointment.
     * Passing Openings' own idempotency key here closes that gap AT the
     * system of record, which is the only place that can actually know
     * whether the write landed.
     *
     * STRICTLY ADDITIVE: optional, and no existing caller passes it — see
     * `createOpeningsBackedAppointment` (`src/lib/api/openingsLocations.ts`),
     * the website's own call site, which never sets this field. Absent
     * `idempotencyKey`, no `Idempotency-Key` header is sent at all — the
     * request is byte-for-byte identical to before this field existed. Pinned
     * by `openingsClient.test.ts`'s "write endpoints" tests (one asserting no
     * header when omitted, one asserting the exact header when present) and
     * by `bookingAdapters.test.ts`'s own assertion that the website's call
     * never includes this key.
     */
    idempotencyKey?: string;
  }) =>
    openingsFetch<OpeningsCreateAppointmentResult>("/v1/appointments/create", {
      method: "POST",
      // `openingsFetch` merges `init.headers` in LAST (see its own comment),
      // so this can never be silently overwritten by the visitor-proxy or
      // Content-Type headers it sets itself. `undefined` here is exactly
      // what every caller that predates this field already sent — no
      // `headers` key in `init` at all — so omitting `idempotencyKey`
      // reproduces prior behavior byte-for-byte.
      headers: args.idempotencyKey
        ? { "Idempotency-Key": args.idempotencyKey }
        : undefined,
      body: JSON.stringify({
        salonId: args.businessId,
        userId: args.userId,
        scheduleId: args.scheduleId,
        customerId: args.customerId,
        teamMemberId: args.teamMemberId,
        services: args.services,
        date: args.date,
        time: args.time,
        verificationCode: args.verificationCode,
        skipPaymentCollection: args.skipPaymentCollection,
        paymentMethodId: args.paymentMethodId,
        metadata: {
          integration: "eddies-edition-website",
          origin: "eddiesedition.com",
          originLabel: "Eddie's Edition website",
          bookingSurface: "pasadena-edition-json-adapter",
          ...(args.metadata ?? {}),
        },
      }),
    }),

  rescheduleAppointment: (args: {
    rescheduleAppointmentId: string;
    businessId: string;
    userId: string;
    scheduleId: string;
    services: { id: string; optionId?: string }[];
    date: string;
    time: string;
    verificationCode?: string;
    phoneNumber?: string;
    paymentMethodId?: string;
    requestId?: string;
    metadata?: OpeningsAppointmentMetadata;
  }) =>
    openingsFetch<OpeningsRescheduleAppointmentResult>(
      "/v1/appointments/reschedule",
      {
        method: "POST",
        body: JSON.stringify({
          rescheduleAppointmentId: args.rescheduleAppointmentId,
          salonId: args.businessId,
          userId: args.userId,
          scheduleId: args.scheduleId,
          services: args.services,
          date: args.date,
          time: args.time,
          verificationCode: args.verificationCode,
          phoneNumber: args.phoneNumber,
          paymentMethodId: args.paymentMethodId,
          requestId: args.requestId,
          metadata: {
            integration: "eddies-edition-website",
            origin: "eddiesedition.com",
            originLabel: "Eddie's Edition website",
            bookingSurface: "pasadena-edition-json-adapter",
            ...(args.metadata ?? {}),
          },
        }),
      },
    ),

  /**
   * `POST /v1/appointments/cancel` — new for Task 8, purely additive (a
   * brand-new export; no existing caller touched). Verified against
   * `cancelHandler.ts` directly: `code` is REQUIRED by Openings' own
   * `CancelAppointmentSchema` (`verificationCodeSchema`, no `.optional()`)
   * — there is no staff-auth bypass on this endpoint the way
   * `rescheduleAppointment` has one, and no way to cancel without a code
   * EXCEPT the one case this app's own route exploits: `cancelHandler.ts`
   * checks `appt.status === "CANCELED"` and returns `{ appointmentId,
   * alreadyCanceled: true }` BEFORE it ever looks at `code` at all — so a
   * genuine double-cancel succeeds even with a stale/placeholder code. The
   * verification is `consumeVerificationCode(identifier, \`cancel:
   * ${appointmentId}\`, code)` — a DIFFERENT purpose string from both this
   * app's own `booking:<businessId>` (used by `/verify/send`) and
   * `rescheduleAppointment`'s own `rescheduleLookup:<salonId>:<customerId>`
   * — see `src/app/api/mobile/v1/appointments/[id]/route.ts`'s module doc
   * for what that means for this route's own DELETE verb.
   */
  cancelAppointment: (args: {
    appointmentId: string;
    code: string;
    reasonCode?: "UNSPECIFIED" | "SICK" | "SCHEDULE_CONFLICT" | "MOVED" | "OTHER";
    reasonNote?: string;
  }) =>
    openingsFetch<OpeningsCancelAppointmentResult>("/v1/appointments/cancel", {
      method: "POST",
      body: JSON.stringify({
        appointmentId: args.appointmentId,
        code: args.code,
        reasonCode: args.reasonCode,
        reasonNote: args.reasonNote,
      }),
    }),

  /**
   * `POST /v1/offers/{offerId}/codes` — mint (or, on an `externalRef`
   * replay, RE-return) `business`'s welcome code for `phoneHash`.
   * Welcome-coupon plan Task 3: this file stays a pure HTTP layer, with no
   * ledger/guard knowledge of its own — `WelcomeMinter.mint`
   * (`src/lib/mobile/welcomeCoupon.ts`, wired by Task 5) is the only
   * caller, and this method's job ends at forwarding the request and
   * returning `{code, expiresAt}` or letting an upstream error propagate.
   *
   * - NO `Idempotency-Key` header. Idempotency comes from `externalRef`
   *   alone — the `@@unique([offerId, externalRef])` constraint on the
   *   Openings side (state-machine.md As-built #5): a replay for the same
   *   phone+offer returns the SAME code (As-built #4), which is what makes
   *   `claimWelcomeCoupon`'s ledger-write-failure recovery safe, and its
   *   P2002 return-existing path correctly SKIPS REVOKED rows and mints
   *   fresh after a revoke. An `Idempotency-Key` here does NOT add
   *   belt-and-braces over that — Openings caches a successful mint
   *   response by that header for 24h and REPLAYS it in a preHandler
   *   BEFORE the `externalRef` logic runs. With a key composed only from
   *   `<phoneHash>:<offerId>` (a pure function of the phone — this route
   *   takes no other caller input), claim at business A (code cached) →
   *   switch to B (A's code REVOKED) → switch back to A within 24h replays
   *   the cached response: the now-REVOKED code, recorded as freshly
   *   minted. The ledger then records a dead code as live, and the card is
   *   bricked until the cache entry expires. (Final-review finding,
   *   2026-08-20.) Openings' replay cache must never be keyed onto a mint
   *   whose validity can be revoked out-of-band — so this header stays
   *   off this call, deliberately, and `openingsClient.test.ts` pins its
   *   absence.
   * - `recipient.phoneNumber` is the customer's RAW phone, and Openings
   *   will not mint without it: `POST /v1/offers` hardcodes
   *   `recipientRequired: input.codeKind === "PERSONAL"`, and
   *   `issuePersonalCode` then rejects any recipient-less mint on such an
   *   offer with `422 VALIDATION_ERROR` ("Please enter a valid recipient
   *   phone number."). `recipientRequired` is absent from
   *   `offerUpdateSchema` too, so it cannot be turned off after creation —
   *   a welcome offer is a PERSONAL offer, so sending the phone is the
   *   only shape Openings accepts. (Found in production 2026-09-19: every
   *   claim 503'd on this 422.) `externalRef` stays the PHONE HASH
   *   regardless — it is the idempotency key, and the ledger key it
   *   corresponds to (`welcomeCoupon.ts`) never holds a raw phone. Sending
   *   the phone here also assigns the code to Openings' own `Customer`
   *   record for this salon, which is what links a redemption back to the
   *   person who claimed it.
   * - Bearer secret is per-business/per-offer
   *   (`resolveWelcomeOfferConfig`): it authorizes only its OWN offer's
   *   codes, matching how the revoke route below scopes by offer id too
   *   (As-built #1).
   * - No `options` argument, so `openingsFetch` takes its uncached
   *   `no-store` branch — deliberate: this is a mutation with a real side
   *   effect (money-adjacent), never a candidate for the data cache.
   * - `signal: AbortSignal.timeout(...)` — see `WELCOME_OFFER_HTTP_TIMEOUT_MS`
   *   above for why this number matters well beyond "don't hang forever".
   */
  mintWelcomeCode: async (args: {
    business: EditionBusinessKey;
    phoneHash: string;
    phoneNumber: string;
  }): Promise<OpeningsWelcomeCodeMintResult> => {
    const config = resolveWelcomeOfferConfig(args.business);
    if (!config) throw notConfiguredError(args.business);
    return openingsFetch<OpeningsWelcomeCodeMintResult>(
      `/v1/offers/${encodeURIComponent(config.offerId)}/codes`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${config.secret}`,
        },
        body: JSON.stringify({
          externalRef: args.phoneHash,
          recipient: { phoneNumber: args.phoneNumber },
        }),
        signal: AbortSignal.timeout(WELCOME_OFFER_HTTP_TIMEOUT_MS),
      },
    );
  },

  /**
   * `DELETE /v1/offers/{offerId}/codes/{code}` — revokes `code` at
   * `business`'s offer. Path shape is an as-built correction over the
   * original spec (state-machine.md As-built #1: offer id in the path, NOT
   * `/v1/offers/codes/{code}`) — the secret authorizes only its own offer's
   * codes, so the offer id has to be part of the URL the secret is checked
   * against. Revoking also frees the code's `externalRef` slot upstream
   * (As-built #2), which is what lets the switch-location path in
   * `welcomeCoupon.ts` re-mint at the SAME offer later.
   *
   * A 409 (code already redeemed, As-built #3) or 404 (already gone) is
   * NOT special-cased here — both propagate as ordinary `{status, body}`
   * errors, `openingsFetch`'s standard contract. Interpreting those codes
   * (409 -> `already_used`, 404 -> "proceed, nothing to revoke") is
   * `claimWelcomeCoupon`'s job, exactly as `WelcomeMinter`'s own doc
   * requires: this file forwards status, it never reads it.
   */
  revokeWelcomeCode: async (args: {
    business: EditionBusinessKey;
    code: string;
  }): Promise<OpeningsWelcomeCodeRevokeResult> => {
    const config = resolveWelcomeOfferConfig(args.business);
    if (!config) throw notConfiguredError(args.business);
    return openingsFetch<OpeningsWelcomeCodeRevokeResult>(
      `/v1/offers/${encodeURIComponent(config.offerId)}/codes/${encodeURIComponent(args.code)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${config.secret}`,
        },
        signal: AbortSignal.timeout(WELCOME_OFFER_HTTP_TIMEOUT_MS),
      },
    );
  },

  recordPhotoViews: (body: PhotoViewsBody) =>
    openingsFetch<{ recorded: number }>("/v1/photos/views", {
      method: "POST",
      credentials: "omit",
      keepalive: true,
      body: JSON.stringify(body),
    }),

  getPhotos: (args: {
    businessId: string;
    limit: number;
    cursor?: string;
    category?: string;
    deviceId?: string;
    memberId?: string;
  }) => {
    const query = new URLSearchParams({
      salonId: args.businessId,
      limit: String(args.limit),
    });
    if (args.cursor) query.set("cursor", args.cursor);
    if (args.category && args.category !== "all") {
      query.set("category", args.category);
    }
    if (args.deviceId) query.set("deviceId", args.deviceId);
    if (args.memberId) query.set("memberId", args.memberId);
    return openingsFetch<OpeningsPhotosListResponse>(
      `/v1/photos?${query.toString()}`,
      undefined,
      args.deviceId
        ? undefined
        : { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    );
  },

  getPhotoCategories: (businessId: string) =>
    openingsFetch<OpeningsPhotoCategoriesResponse>(
      `/v1/photos/categories?salonId=${encodeURIComponent(businessId)}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),

  getLikedPhotoIds: (args: { businessId: string; deviceId: string }) => {
    const query = new URLSearchParams({
      salonId: args.businessId,
      deviceId: args.deviceId,
    });
    return openingsFetch<OpeningsPhotoLikesResponse>(
      `/v1/photos/likes?${query.toString()}`,
    );
  },

  likePhoto: (args: { photoId: string; deviceId: string }) =>
    openingsFetch<OpeningsPhotoLikeResult>(
      `/v1/photos/${encodeURIComponent(args.photoId)}/like`,
      {
        method: "POST",
        body: JSON.stringify({ deviceId: args.deviceId }),
      },
    ),

  unlikePhoto: (args: { photoId: string; deviceId: string }) =>
    openingsFetch<OpeningsPhotoLikeResult>(
      `/v1/photos/${encodeURIComponent(args.photoId)}/unlike`,
      {
        method: "POST",
        body: JSON.stringify({ deviceId: args.deviceId }),
      },
    ),

  getBlogPosts: (args: {
    businessId: string;
    limit?: number;
    cursor?: string;
  }) => {
    const query = new URLSearchParams();
    if (args.limit) query.set("limit", String(args.limit));
    if (args.cursor) query.set("cursor", args.cursor);

    const suffix = query.toString() ? `?${query.toString()}` : "";
    return openingsFetch<OpeningsBlogPostsListResponse>(
      `/v1/salons/${encodeURIComponent(args.businessId)}/blog/posts${suffix}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    );
  },

  getBlogPost: (args: { businessId: string; slug: string }) =>
    openingsFetch<OpeningsBlogPostDetailResponse>(
      `/v1/salons/${encodeURIComponent(args.businessId)}/blog/posts/${encodeURIComponent(args.slug)}`,
      undefined,
      { revalidate: PUBLIC_CONTENT_REVALIDATE_SECONDS },
    ),
};
